/* eslint-disable */
import { Billing, PlaidEnvironment } from '../api/internal-billing.api';
import { injectable, inject } from '@servicetitan/react-ioc';
import axios, { AxiosResponse } from 'axios';

declare global {
    interface Window {
        Plaid: any;
    }
}
export enum PlaidEnvironmentType {
    Sandbox = 'sandbox',
    Development = 'development',
    Production = 'production',
}

export enum PlaidHost {
    Sandbox = 'https://sandbox.plaid.com',
    Development = 'https://development.plaid.com',
    Production = 'https://production.plaid.com',
}

export enum PlaidProducts {
    Transactions = 'transactions',
    Auth = 'auth',
    Identity = 'identity',
    Income = 'income',
    Assets = 'assets',
}
export interface PlaidInstitutionResponse {
    institution: {
        logo: string;
        [index: string]: any;
    };
}

export interface PlaidError {
    display_message: string;
    error_code: string;
    error_message: string;
    error_type: string;
}

export interface MetaData {
    [key: string]: any;
}

export interface SupportedPlaidInterface {
    onExit: (error: PlaidError | null, metadata?: MetaData) => void;
    onLoad?: () => void;
    onEvent?: (eventName: string, metadata?: MetaData) => void;
    onSuccess: (publicToken: string, metadata?: MetaData) => void;
}

interface Institution {
    name: string;
    id: string;
    apiV2Id: string;
    [index: string]: any;
}

export interface PlaidInstance {
    open(): void;
    exit(): void;
    institutions: Institution[];
}

interface PlaidConfigurations {
    product: PlaidProducts[];
    clientName: string;
    selectAccount: boolean;
    env?: PlaidEnvironmentType;
    key?: string;
    webhook?: string;
    user: PlaidUserConfiguration;
    token?: string;
    countryCodes?: string[];
}

interface PlaidUserConfiguration {
    legalName: string;
    emailAddress: string;
}

@injectable()
export class PlaidIntegrations {
    private static plaidInstance?: PlaidInstance;
    private static plaidScript = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    private static scriptInjected = false;
    private static plaidHost: PlaidHost;
    private static plaidPromise?: Promise<void>;
    private static plaidConfigurations: PlaidConfigurations = {
        product: [PlaidProducts.Auth],
        countryCodes: ['US'],
        clientName: 'ServiceTitan',
        selectAccount: true,
        user: { legalName: 'Sergey Sokratov', emailAddress: 'ssokratov@linxlogic.com' },
    };

    constructor(@inject(Billing) private billingApi: Billing) {}

    resetPlaidIntegration = () => {
        PlaidIntegrations.plaidConfigurations.key = undefined;
    };

    private setEnvironmentVariables(
        environment: PlaidEnvironment,
        webhooks?: string,
        publicKey?: string,
        legalName?: string,
        email?: string,
        publicToken?: string
    ) {
        if (publicKey) {
            PlaidIntegrations.plaidConfigurations.key = publicKey;
        }
        switch (environment) {
            case PlaidEnvironment.Production:
                PlaidIntegrations.plaidHost = PlaidHost.Production;
                PlaidIntegrations.plaidConfigurations.env = PlaidEnvironmentType.Production;
                break;
            case PlaidEnvironment.Development:
                PlaidIntegrations.plaidConfigurations.env = PlaidEnvironmentType.Development;
                PlaidIntegrations.plaidHost = PlaidHost.Development;
                break;
            default:
                PlaidIntegrations.plaidConfigurations.env = PlaidEnvironmentType.Sandbox;
                PlaidIntegrations.plaidHost = PlaidHost.Sandbox;
        }

        PlaidIntegrations.plaidConfigurations.webhook = webhooks;
        PlaidIntegrations.plaidConfigurations.user.legalName = legalName || '';
        PlaidIntegrations.plaidConfigurations.user.emailAddress = email || '';
        PlaidIntegrations.plaidConfigurations.token = publicToken;
    }

    private async createPlaidInstance(events: SupportedPlaidInterface) {
        if (!PlaidIntegrations.plaidConfigurations.key) {
            const plaidCredentials = (await this.billingApi.getPlaidCredentials()).data;
            this.setEnvironmentVariables(
                plaidCredentials!.Environment,
                plaidCredentials!.AuthWebhook,
                plaidCredentials!.PublicKey,
                plaidCredentials!.LegalName,
                plaidCredentials!.Email,
                plaidCredentials!.PublicToken
            );
        }

        PlaidIntegrations.plaidInstance = window.Plaid.create({
            ...PlaidIntegrations.plaidConfigurations,
            onExit: events.onExit,
            onLoad: events.onLoad,
            onEvent: events.onEvent,
            onSuccess: events.onSuccess,
        });
    }

    private injectPlaidScript(): Promise<void> {
        if (!PlaidIntegrations.plaidPromise) {
            PlaidIntegrations.plaidPromise = new Promise((resolve, reject) => {
                if (!PlaidIntegrations.scriptInjected) {
                    const script = document.createElement('script');
                    script.src = PlaidIntegrations.plaidScript;
                    script.setAttribute('type', 'text/javascript');
                    script.onerror = (e: string | Event) => {
                        console.log('Stripe loading failed');
                        PlaidIntegrations.plaidPromise = undefined;
                        PlaidIntegrations.scriptInjected = false;
                        reject(e);
                    };
                    script.onload = () => {
                        PlaidIntegrations.scriptInjected = true;
                        PlaidIntegrations.plaidPromise = undefined;
                        resolve();
                    };

                    document.body.appendChild(script);
                } else {
                    resolve();
                }
            });
        }
        return PlaidIntegrations.plaidPromise;
    }

    async getPlaidInstance(events: SupportedPlaidInterface): Promise<PlaidInstance> {
        await this.injectPlaidScript();
        await this.createPlaidInstance(events);
        return PlaidIntegrations.plaidInstance!;
    }

    async getBankLogo(institutionId: string): Promise<AxiosResponse<PlaidInstitutionResponse>> {
        if (!PlaidIntegrations.plaidConfigurations.key) {
            const plaidCredentials = (await this.billingApi.getPlaidCredentials()).data;
            this.setEnvironmentVariables(
                plaidCredentials!.Environment,
                plaidCredentials!.AuthWebhook,
                plaidCredentials!.PublicKey,
                plaidCredentials!.LegalName,
                plaidCredentials!.Email
            );
        }
        return axios.post(
            PlaidIntegrations.plaidHost + '/institutions/get_by_id',
            JSON.stringify({
                institution_id: institutionId,
                public_key: PlaidIntegrations.plaidConfigurations.key,
                options: { include_display_data: true },
            }),
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
    }
}
